import React, { useContext, useEffect, useState } from "react";
import {
  Collapse,
  Row,
  Col,
  Statistic,
  Tag,
  Image,
  Button,
  Spin,
  Divider,
} from "antd";
import {
  CloseOutlined,
  FilePdfOutlined,
  CheckOutlined,
  CaretRightOutlined,
  CloseCircleFilled,
  CheckCircleFilled,
} from "@ant-design/icons";
import { UserContext } from "../../../context";
import { TContext, TResponseModel } from "../../../types/config";
import { TScanResult } from "../../../types";
import useFetch from "../../../hooks/useFetch";
import useToast from "../../../hooks/useToast";
import {
  GetTimeLineDetails,
  GetUnitProgressReport,
} from "../../../utils/api.constant";
import {
  eEVAL_L1_WORKFLOW,
  eHTTPStatusCode,
  eOUTPUT_STATUS,
  eOVERALLISTSTATUS,
  eResultCode,
  eWARRANTY_STATUS,
} from "../../../utils/enum";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import DateUtils from "../../../utils/date";
import styles from "./eval.module.css";
import TimeLine from "./time-line";
import AuthUtil from "../../../utils/auth";
import { UNAUTHORIZEDREQUEST } from "../../../utils/constants";
const { Panel } = Collapse;

type TProps = {
  id?: number;
  onCloseDrawer?: () => void;
};

export type TTimeLineModel = {
  currentStageId: eOVERALLISTSTATUS;
  submissionStatus: eEVAL_L1_WORKFLOW;
  scannedProductInfo?: TScanResult;
  evalL1Scan?: TStageL1Scan;
  hiPot?: TStageHiPot;
  visualInspection?: TStageVisualInspection;
  performanceTesting?: TStagePerformanceTesting;
  disposition?: TStageDisposition;
  fireId: string;
  customerRecordNumber: string;
  customerName: string;
  evalL2Data?: TEvalL2Data;
};

export type TEvalL2Data = {
  evalL2Description: string;
  stageName: string;
};
export type TStageL1Scan = {
  stageName?: string;
  evaluationDate?: string;
  typeOfUnit?: string;
  airtechModel?: string;
  modelNoOfUnit?: string;
  serialNumber?: string;
  warrantyStatus?: string;
};

export type TStageHiPot = {
  stageName?: string;
  hiPotStatus?: string;
  noOfTimesStatorUsed?: string;
  rustInConnectorStatus?: string;
  rustImageDetails?: TRustImages;
};

export type TStageVisualInspection = {
  stageName?: string;
  visualImageDescription?: string;
  visualInspectionFileArray?: string[];
};

export type TRustImages = {
  rustImageDescription?: string;
  rustImageFileArray?: string[];
};

export type TStagePerformanceTesting = {
  stageName?: string;
  performanceTestingStatus?: string;
  faultCategoryName?: string;
  testStandResult?: string;
  faultAdditionalNotes?: string;
};

export type TStageDisposition = {
  stageName?: string;
  signsOfCarbonResidue?: string;
  verifyCarbonResidue?: string;
  verifyCutInUnit?: string;
  carbonResidueImageDescription?: string;
  carbonResidueImageFileArray?: string[];
};

function EvalLevel1View(props: TProps) {
  const context: TContext = useContext(UserContext);
  const { post } = useFetch();
  const { onShowToast } = useToast();

  const [activeKeys, setActiveKeys] = useState<string[]>([
    "2",
    "3",
    "4",
    "5",
    "6",
  ]);
  const [loading, setLoading] = useState<boolean>(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [timeLineDetails, setTimeLineDetails] = useState<TTimeLineModel | null>(
    null
  );

  useEffect(() => {
    if (!props.id) return;
    fetchTimeline();
  }, [props.id]);

  const fetchTimeline = async () => {
    try {
      setLoading(true);

      const payload = {
        data: {
          id: props.id,
        },
      };

      const res: TResponseModel = await post(GetTimeLineDetails, payload);

      if (res.dataResponse.returnCode === eResultCode.SUCCESS) {
        const apiData = res.data;

        const formatted: TTimeLineModel = {
          scannedProductInfo: apiData.scannedProductInfo,
          evalL1Scan: apiData.evalL1Scan,
          hiPot: apiData.hiPot,
          visualInspection: apiData.visualInspection,
          performanceTesting: apiData.performanceTesting,
          disposition: apiData.disposition,
          ...apiData,
        };

        setTimeLineDetails(formatted);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: res.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
      onShowToast({
        type: ToastType.error,
        title: <CloseCircleFilled />,
        position: ToastOpen.leftBottom,
        content: "Failed to load timeline details.",
      });
    } finally {
      setLoading(false);
    }
  };

  const panelStyle: React.CSSProperties = {
    marginBottom: 8,
    background: "#fff",
    borderRadius: "1px",
    border: "none",
  };

  const tagColor = (status?: string) => {
    if (!status) return "blue";

    const normalized = status.toLowerCase();

    if (["pass", "passed", "valid"].includes(normalized)) return "green";
    if (["fail", "failed", "invalid"].includes(normalized)) return "red";
    if (["no fault found", "not detected"].includes(normalized))
      return "orange";

    return "blue";
  };

  const renderField = (
    title: string,
    value?: string | number | null,
    span: number = 6
  ) => {
    if (value === undefined || value === null || value === "") return null;
    return (
      <Col xs={12} sm={12} md={8} lg={span} style={{ paddingBottom: "20px" }}>
        <Statistic
          title={title}
          value={value}
          formatter={(val) => String(val)}
        />
      </Col>
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "2rem" }}>
        <Spin tip="Loading timeline details..." />
      </div>
    );
  }

  const getStatusColor = (status: eOVERALLISTSTATUS) => {
    switch (status) {
      case eOVERALLISTSTATUS.NOT_SCANNED:
        return "#B0BEC5";
      case eOVERALLISTSTATUS.SCANNED_WAITING_EVAL:
        return "#FFB74D";
      case eOVERALLISTSTATUS.EVAL_L1:
        return "#42A5F5";
      case eOVERALLISTSTATUS.EVAL_L2:
        return "#7E57C2";
      case eOVERALLISTSTATUS.NO_FAULT:
        return "#66BB6A";
      case eOVERALLISTSTATUS.TEAR_DOWN:
        return "#EF5350";
      case eOVERALLISTSTATUS.ALL:
        return "#78909C";
      default:
        return "#9E9E9E";
    }
  };

  const tabs = [
    { label: "Not Scanned", value: eOVERALLISTSTATUS.NOT_SCANNED },
    {
      label: "Scanned-Waiting Eval",
      value: eOVERALLISTSTATUS.SCANNED_WAITING_EVAL,
    },
    { label: "Eval - L1", value: eOVERALLISTSTATUS.EVAL_L1 },
    { label: "Eval - L2", value: eOVERALLISTSTATUS.EVAL_L2 },
    { label: "No Fault", value: eOVERALLISTSTATUS.NO_FAULT },

    { label: "Tear Down", value: eOVERALLISTSTATUS.TEAR_DOWN },
    { label: "All", value: eOVERALLISTSTATUS.ALL },
  ];
  // if (!timeLineDetails) {
  //   return (
  //     <div style={{ textAlign: "center", padding: "2rem" }}>
  //       <p>No timeline data available for this record.</p>
  //     </div>
  //   );
  // }

  const generateInvoicePdf = async () => {
    try {
      setPdfLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_PUBLIC_API_BASEURL}${GetUnitProgressReport}`,
        {
          method: "POST", // Change to POST
          headers: {
            "Content-Type": "application/json", // Set content type for JSON payload
            ...AuthUtil.getToken(),
          },
          body: JSON.stringify({
            data: { id: props?.id },
          }),
        }
      );

      // Check if the response is OK
      if (response.status === eHTTPStatusCode.OK) {
        // Convert response to Blob
        const blob = await response.blob();

        // Create a URL for the Blob
        const url = window.URL.createObjectURL(blob);

        // Create a link element and set the URL as href
        const link = document.createElement("a");
        link.href = url;

        // Set the file name for the download
        link.download = `${timeLineDetails?.scannedProductInfo?.serialNumber}.pdf`; // Set the desired file name with .pdf extension

        // Append the link to the document and trigger the download
        document.body.appendChild(link);
        link.click();

        // Clean up by removing the link and revoking the URL
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Show a success toast message
        onShowToast({
          type: ToastType.success,
          title: <CheckCircleFilled />,
          position: ToastOpen.leftBottom,
          content: "Unit progress report downloaded successfully",
        });
      } else if (response.status === eHTTPStatusCode.UNAUTHORIZED_REQUEST) {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: UNAUTHORIZEDREQUEST,
        });
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: "Error while downloading file, please try again later.",
        });
      }
    } catch (error) {
      // Handle any exceptions
      onShowToast({
        type: ToastType.error,
        title: <CloseCircleFilled />,
        position: ToastOpen.leftBottom,
        content: "Error downloading PDF file",
      });
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>Unit Progress Summary</h3>
        <div className={styles.headerButtons}>
          {timeLineDetails?.currentStageId ? (
            <Tag
              color={getStatusColor(
                timeLineDetails?.currentStageId as eOVERALLISTSTATUS
              )}
            >
              {timeLineDetails?.currentStageId
                ? tabs.find((t) => t.value === timeLineDetails?.currentStageId)
                    ?.label
                : "-"}
            </Tag>
          ) : null}
          {timeLineDetails?.submissionStatus === eEVAL_L1_WORKFLOW.DRAFT ? (
            <Tag color="yellow">Draft</Tag>
          ) : timeLineDetails?.submissionStatus ===
            eEVAL_L1_WORKFLOW.SUBMITTED ? (
            <Tag color="green">Submitted</Tag>
          ) : (
            ""
          )}
          <small
            onClick={() => setActiveKeys([])}
            style={{ cursor: "pointer" }}
          >
            Collapse All
          </small>
          |
          <small
            onClick={() => setActiveKeys(["1", "2", "3", "4", "5", "6", "7"])}
            style={{ cursor: "pointer", marginRight: "10px" }}
          >
            Expand All
          </small>
          {props?.id ? (
            <Button
              type="primary"
              loading={pdfLoading}
              onClick={() => {
                if (props?.id) {
                  generateInvoicePdf();
                } else {
                  console.error("Missing eval details");
                }
              }}
            >
              {pdfLoading ? "Downloading" : "Download"} <FilePdfOutlined />
            </Button>
          ) : null}
          {props.onCloseDrawer && (
            <Button
              type="default"
              icon={<CloseOutlined />}
              onClick={props.onCloseDrawer}
            >
              Close
            </Button>
          )}
        </div>
      </div>
      {timeLineDetails ? (
        <Collapse
          activeKey={activeKeys}
          onChange={(keys) => setActiveKeys(keys as string[])}
          bordered={false}
          expandIcon={({ isActive }) => (
            <CaretRightOutlined rotate={isActive ? 90 : 0} />
          )}
          // expandIconPosition="end"
        >
          {/*  Scanned Product Info */}
          {timeLineDetails.scannedProductInfo &&
            (timeLineDetails.scannedProductInfo?.serialNumber ||
              timeLineDetails.scannedProductInfo?.airtechModel ||
              timeLineDetails.scannedProductInfo?.partNumber ||
              timeLineDetails.scannedProductInfo?.warrantyStatus ||
              timeLineDetails.scannedProductInfo?.manufacturingDateString ||
              timeLineDetails.scannedProductInfo?.warrantyDateString ||
              timeLineDetails.scannedProductInfo?.debriefDateString ||
              timeLineDetails.scannedProductInfo?.scannedDate ||
              timeLineDetails.scannedProductInfo?.excelMessage) && (
              <Panel
                key="1"
                style={panelStyle}
                header={
                  <span className={styles.panelHeader}>
                    Scanned Product Information
                  </span>
                }
              >
                <Row gutter={16}>
                  {renderField(
                    "Serial Number",
                    timeLineDetails.scannedProductInfo?.serialNumber
                  )}
                  {renderField(
                    "Model No.",
                    timeLineDetails.evalL1Scan?.modelNoOfUnit
                  )}
                  {renderField(
                    "Part Number",
                    timeLineDetails.scannedProductInfo?.partNumber
                  )}
                  {timeLineDetails.scannedProductInfo?.warrantyStatus && (
                    <Col
                      xs={24}
                      sm={12}
                      md={8}
                      lg={6}
                      style={{ paddingBottom: "20px" }}
                    >
                      <Statistic
                        title="Warranty Status"
                        valueRender={() => {
                          const warrantyStatus =
                            timeLineDetails?.scannedProductInfo?.warrantyStatus;
                          const outputStatus =
                            timeLineDetails?.scannedProductInfo?.outputStatus;

                          let text = "-";
                          if (warrantyStatus === eWARRANTY_STATUS.IN_WARRANTY)
                            text = "In Warranty";
                          else if (
                            warrantyStatus ===
                            eWARRANTY_STATUS.IN_WARRANTY_CLAIMED
                          )
                            text = "In Warranty and Claimed";
                          else if (
                            warrantyStatus ===
                            eWARRANTY_STATUS.IN_WARRANTY_NOT_CLAIMED
                          )
                            text = "In Warranty and Not Claimed";
                          else if (
                            warrantyStatus === eWARRANTY_STATUS.OUT_OF_WARRANTY
                          )
                            text = "Out of Warranty";

                          const backgroundColor =
                            outputStatus === eOUTPUT_STATUS.GREEN
                              ? "var(--bs-success)"
                              : outputStatus === eOUTPUT_STATUS.RED
                                ? "var(--bs-danger)"
                                : "";
                          const color = outputStatus ? "white" : "#333";

                          return (
                            <span
                              style={{
                                backgroundColor,
                                color,
                                padding: "2px 4px",
                                borderRadius: "2px",
                              }}
                            >
                              {text}
                            </span>
                          );
                        }}
                      />
                    </Col>
                  )}
                </Row>
                <Row gutter={16}>
                  {renderField(
                    "Manufacturing Date",
                    timeLineDetails.scannedProductInfo?.manufacturingDateString
                  )}
                  {renderField(
                    "Warranty Date",
                    timeLineDetails.scannedProductInfo?.warrantyDateString
                  )}
                  {renderField(
                    "Debrief Date",
                    timeLineDetails.scannedProductInfo?.debriefDateString
                  )}
                  {renderField(
                    "Scanned Date & Time",
                    DateUtils.format(
                      timeLineDetails.scannedProductInfo?.scannedDate,
                      context?.user?.dateFormat,
                      true
                    )
                  )}
                  {renderField(
                    "Excel Message",
                    timeLineDetails.scannedProductInfo?.excelMessage,
                    12
                  )}
                  {renderField("FiRe ID", timeLineDetails.fireId, 6)}
                  {renderField(
                    "Customer Record No.",
                    timeLineDetails.customerRecordNumber?.toString(),
                    6
                  )}
                </Row>
              </Panel>
            )}

          {/* L1 Evaluation */}
          {timeLineDetails.evalL1Scan &&
            (timeLineDetails.evalL1Scan.evaluationDate ||
              timeLineDetails.evalL1Scan.typeOfUnit ||
              timeLineDetails.evalL1Scan.modelNoOfUnit ||
              timeLineDetails.evalL1Scan.warrantyStatus) && (
              <Panel
                key="2"
                style={panelStyle}
                header={
                  <span className={styles.panelHeader}>Eval-L1 Status</span>
                }
              >
                <Row gutter={16}>
                  {renderField(
                    "Evaluation Date",
                    timeLineDetails.evalL1Scan?.evaluationDate
                      ? DateUtils.format(
                          timeLineDetails.evalL1Scan?.evaluationDate,
                          context?.user?.dateFormat
                        )
                      : "-"
                  )}

                  {renderField("Customer", timeLineDetails.customerName)}
                  {renderField(
                    "Type of Unit",
                    timeLineDetails.evalL1Scan?.typeOfUnit
                  )}

                  {/* {renderField(
                    "Airtech Model",
                    timeLineDetails.evalL1Scan?.airtechModel
                  )} */}

                  {timeLineDetails.evalL1Scan?.modelNoOfUnit && (
                    <Col
                      xs={24}
                      sm={12}
                      md={8}
                      lg={6}
                      style={{ paddingBottom: "20px" }}
                    >
                      <Statistic
                        title="Product"
                        valueRender={() => (
                          <span>
                            {timeLineDetails.evalL1Scan?.airtechModel} (
                            {timeLineDetails.evalL1Scan?.modelNoOfUnit || "-"})
                          </span>
                        )}
                      />
                    </Col>
                  )}

                  {/* {timeLineDetails.evalL1Scan?.warrantyStatus && (
                    <Col
                      xs={24}
                      sm={12}
                      md={8}
                      lg={6}
                      style={{ paddingBottom: "20px" }}
                    >
                      <Statistic
                        title="Warranty Status"
                        valueRender={() => (
                          <Tag color="blue">
                            {timeLineDetails.evalL1Scan?.warrantyStatus}
                          </Tag>
                        )}
                      />
                    </Col>
                  )} */}
                </Row>
                {/* HI-POT Status */}

                {timeLineDetails.hiPot?.stageName ? (
                  <Divider orientation="left">
                    {timeLineDetails.hiPot?.stageName?.toUpperCase()}
                  </Divider>
                ) : null}
                <Row gutter={16}>
                  {timeLineDetails?.hiPot?.hiPotStatus && (
                    <Col
                      xs={12}
                      sm={12}
                      md={8}
                      lg={6}
                      style={{ paddingBottom: "20px" }}
                    >
                      <Statistic
                        title="HiPot Status"
                        valueRender={() => (
                          <Tag
                            color={tagColor(timeLineDetails.hiPot?.hiPotStatus)}
                          >
                            {timeLineDetails.hiPot?.hiPotStatus}
                          </Tag>
                        )}
                      />
                    </Col>
                  )}
                  {renderField(
                    "Stator Used (Times)",
                    timeLineDetails.hiPot?.noOfTimesStatorUsed
                  )}
                  {renderField(
                    "Rust Status",
                    timeLineDetails.hiPot?.rustInConnectorStatus
                  )}
                </Row>

                {(timeLineDetails.hiPot?.rustImageDetails
                  ?.rustImageDescription ||
                  (timeLineDetails.hiPot?.rustImageDetails?.rustImageFileArray
                    ?.length ?? 0) > 0) && (
                  <Row gutter={16}>
                    <Col span={24}>
                      {timeLineDetails.hiPot?.rustImageDetails
                        ?.rustImageDescription && (
                        <Statistic
                          title="Rust Inspection Notes"
                          value={
                            timeLineDetails.hiPot?.rustImageDetails
                              ?.rustImageDescription
                          }
                        />
                      )}
                      {(timeLineDetails.hiPot?.rustImageDetails
                        ?.rustImageFileArray?.length ?? 0) > 0 && (
                        <div
                          style={{ display: "flex", gap: 10, marginTop: 10 }}
                        >
                          {timeLineDetails?.hiPot?.rustImageDetails?.rustImageFileArray?.map(
                            (img, i) => (
                              <Image key={i} width={60} height={60} src={img} />
                            )
                          )}
                        </div>
                      )}
                    </Col>
                  </Row>
                )}

                {/* Visual Inspection */}
                {timeLineDetails.visualInspection?.stageName ? (
                  <Divider orientation="left">
                    {timeLineDetails.visualInspection?.stageName?.toUpperCase()}
                  </Divider>
                ) : null}
                <Row gutter={16}>
                  {renderField(
                    "Description",
                    timeLineDetails.visualInspection?.visualImageDescription,
                    24
                  )}
                  {(timeLineDetails.visualInspection?.visualInspectionFileArray
                    ?.length ?? 0) > 0 && (
                    <Col span={24}>
                      <div style={{ display: "flex", gap: 10 }}>
                        {timeLineDetails?.visualInspection?.visualInspectionFileArray?.map(
                          (img, i) => (
                            <Image key={i} width={60} height={60} src={img} />
                          )
                        )}
                      </div>
                    </Col>
                  )}
                </Row>

                {/* Performance Testing */}
                {timeLineDetails.performanceTesting?.stageName ? (
                  <Divider orientation="left">
                    {timeLineDetails.performanceTesting?.stageName?.toUpperCase()}
                  </Divider>
                ) : null}
                <Row gutter={16}>
                  {timeLineDetails.performanceTesting
                    ?.performanceTestingStatus && (
                    <Col
                      xs={24}
                      sm={12}
                      md={8}
                      lg={6}
                      style={{ paddingBottom: "20px" }}
                    >
                      <Statistic
                        title="Status"
                        valueRender={() => (
                          <Tag
                            color={tagColor(
                              timeLineDetails.performanceTesting
                                ?.performanceTestingStatus
                            )}
                          >
                            {
                              timeLineDetails.performanceTesting
                                ?.performanceTestingStatus
                            }
                          </Tag>
                        )}
                      />
                    </Col>
                  )}
                  {renderField(
                    "Fault Category",
                    timeLineDetails.performanceTesting?.faultCategoryName
                  )}
                  {renderField(
                    "Additional Notes",
                    timeLineDetails.performanceTesting?.faultAdditionalNotes,
                    12
                  )}
                  {renderField(
                    "Test Stand Result",
                    timeLineDetails.performanceTesting?.testStandResult
                  )}
                </Row>
                {/* Final Disposition */}
                {timeLineDetails.disposition?.stageName ? (
                  <Divider orientation="left">
                    {timeLineDetails.disposition?.stageName?.toUpperCase()}
                  </Divider>
                ) : null}
                <Row gutter={16}>
                  {renderField(
                    "Signs of Carbon Residue",
                    timeLineDetails.disposition?.signsOfCarbonResidue
                  )}
                  {renderField(
                    "Carbon Residue Verification",
                    timeLineDetails.disposition?.verifyCarbonResidue
                  )}
                  {renderField(
                    "Cut Verification",
                    timeLineDetails.disposition?.verifyCutInUnit
                  )}
                </Row>

                {(timeLineDetails.disposition?.carbonResidueImageDescription ||
                  (timeLineDetails.disposition?.carbonResidueImageFileArray
                    ?.length ?? 0) > 0) && (
                  <Row gutter={16}>
                    {renderField(
                      "Inspection Summary",
                      timeLineDetails?.disposition
                        ?.carbonResidueImageDescription,
                      24
                    )}
                    {(timeLineDetails?.disposition?.carbonResidueImageFileArray
                      ?.length ?? 0) > 0 && (
                      <Col span={24} style={{ marginTop: 10 }}>
                        <div style={{ display: "flex", gap: 10 }}>
                          {timeLineDetails?.disposition?.carbonResidueImageFileArray?.map(
                            (img, i) => (
                              <Image key={i} width={60} height={60} src={img} />
                            )
                          )}
                        </div>
                      </Col>
                    )}
                  </Row>
                )}
              </Panel>
            )}

          {/* HI-POT Test */}
          {/* {timeLineDetails.hiPot &&
            (timeLineDetails.hiPot.hiPotStatus ||
              timeLineDetails.hiPot.noOfTimesStatorUsed ||
              timeLineDetails.hiPot.rustInConnectorStatus ||
              timeLineDetails.hiPot.rustImageDetails) && (
              <Panel
                key="3"
                style={panelStyle}
                header={
                  <span className={styles.panelHeader}>
                    {timeLineDetails.hiPot?.stageName}
                  </span>
                }
              ></Panel>
            )} */}

          {/* Visual Inspection */}
          {/* {timeLineDetails.visualInspection &&
            (timeLineDetails.visualInspection.visualImageDescription ||
              (timeLineDetails.visualInspection.visualInspectionFileArray
                ?.length ?? 0) > 0) && (
              <Panel
                key="4"
                style={panelStyle}
                header={
                  <span className={styles.panelHeader}>
                    {timeLineDetails.visualInspection?.stageName}
                  </span>
                }
              ></Panel>
            )} */}

          {/* Performance Testing */}
          {/* {timeLineDetails.performanceTesting &&
            (timeLineDetails.performanceTesting.performanceTestingStatus ||
              timeLineDetails.performanceTesting.faultCategoryName ||
              timeLineDetails.performanceTesting.testStandResult) && (
              <Panel
                key="5"
                style={panelStyle}
                header={
                  <span className={styles.panelHeader}>
                    {timeLineDetails.performanceTesting?.stageName}
                  </span>
                }
              ></Panel>
            )} */}

          {/*  Final Disposition */}
          {/* {timeLineDetails.disposition &&
            (timeLineDetails.disposition.signsOfCarbonResidue ||
              timeLineDetails.disposition.verifyCarbonResidue ||
              timeLineDetails.disposition.verifyCutInUnit ||
              timeLineDetails.disposition.carbonResidueImageDescription ||
              (timeLineDetails.disposition.carbonResidueImageFileArray
                ?.length ?? 0) > 0) && (
              <Panel
                key="6"
                style={panelStyle}
                header={
                  <span className={styles.panelHeader}>
                    {timeLineDetails.disposition?.stageName}
                  </span>
                }
              ></Panel>
            )} */}

          {timeLineDetails.evalL2Data ? (
            <Panel
              key="7"
              style={panelStyle}
              header={<span className={styles.panelHeader}>Eval-L2-Scan</span>}
            >
              <Row gutter={16}>
                {renderField(
                  "Stage Name",
                  timeLineDetails.evalL2Data.stageName
                )}

                {renderField(
                  "Description",
                  timeLineDetails.evalL2Data.evalL2Description,
                  18
                )}
              </Row>
            </Panel>
          ) : null}

          <Panel
            key="6"
            style={panelStyle}
            header={<span className={styles.panelHeader}>Timeline</span>}
          >
            <TimeLine id={props?.id as number}></TimeLine>
          </Panel>
        </Collapse>
      ) : (
        <div style={{ textAlign: "center", padding: "2rem" }}>
          <h3>No data available for this record.</h3>
        </div>
      )}
    </div>
  );
}

export default EvalLevel1View;
